import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { pushLayer } from './overlayStack';
import './curation.css';

function useLayer(onClose: () => void, busy: boolean) {
  const element = useRef<HTMLElement | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);
  const close = useRef(onClose);
  const isBusy = useRef(busy);
  close.current = onClose;
  isBusy.current = busy;
  useEffect(() => {
    if (!element.current) return undefined;
    return pushLayer(element.current, heading.current, () => close.current(), () => isBusy.current);
  }, []);
  return { element, heading };
}

export interface DrawerProps {
  eyebrow: string;
  title: string;
  subtitle?: React.ReactNode;
  onClose: () => void;
  busy?: boolean;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

export const Drawer: React.FC<DrawerProps> = ({ eyebrow, title, subtitle, onClose, busy = false, footer, children }) => {
  const titleId = useId();
  const { element, heading } = useLayer(onClose, busy);
  return createPortal(
    <div className="cur">
      <div className="cur-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
        <aside ref={element} className="cur-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
          <div className="cur-drawer-head">
            <div className="cur-grow">
              <div className="cur-eyebrow">{eyebrow}</div>
              <h2 id={titleId} ref={heading} tabIndex={-1} className="cur-drawer-title">{title}</h2>
              {subtitle && <div className="cur-drawer-sub">{subtitle}</div>}
            </div>
            <button type="button" className="cur-icon-btn" aria-label="Close" onClick={onClose} disabled={busy}>
              <Icon name="x" />
            </button>
          </div>
          <div className="cur-drawer-body">{children}</div>
          {footer && <div className="cur-drawer-foot">{footer}</div>}
        </aside>
      </div>
    </div>,
    document.body,
  );
};

export interface ModalProps {
  title: string;
  onClose: () => void;
  busy?: boolean;
  footer?: React.ReactNode;
  size?: 'md' | 'wide' | 'xwide';
  children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ title, onClose, busy = false, footer, size = 'md', children }) => {
  const titleId = useId();
  const { element, heading } = useLayer(onClose, busy);
  return createPortal(
    <div className="cur">
      <div className="cur-modal-back" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
        <section ref={element} className={`cur-modal cur-modal--${size}`} role="dialog" aria-modal="true"
          aria-labelledby={titleId} tabIndex={-1}>
          <div className="cur-modal-head">
            <h2 id={titleId} ref={heading} tabIndex={-1}>{title}</h2>
            <button type="button" className="cur-icon-btn" aria-label="Close" onClick={onClose} disabled={busy}>
              <Icon name="x" />
            </button>
          </div>
          <div className="cur-modal-body">{children}</div>
          {footer && <div className="cur-modal-foot">{footer}</div>}
        </section>
      </div>
    </div>,
    document.body,
  );
};
